import React, { RefObject, use } from 'react';
import clsx from 'clsx';
import { ShippingAddress } from '@interfaces/api/responses/user-shipping-address';
import { useTranslation } from 'next-i18next';
import Icon from '@components/atoms/icon/icon';
import PopupActionView from '@components/orders-sales/product-timeline/step-info/popup-action';
import { TimelineShippingLabelAction } from '@interfaces/models/timeline';
import { TimelineContext } from '@context/timeline.context';
import { StepInfoContext } from '@context/step-info.context';
import useAnalyticEvents from '@hooks/analytics/use-analytic-events';
import styles from '../select-carrier-options.module.scss';

interface AddressProps {
  refetch: () => void;
  address?: ShippingAddress;
  action?: TimelineShippingLabelAction;
  updateShipmentBtnRef: RefObject<HTMLButtonElement>;
}

const CLASSNAME = 'shippingAddress';
const LANG_PREFIX = 'MY_ACCOUNT.ORDERS_SALES.POPUP.GET_SHIPPING_LABEL.';

const AddressCard = ({ address, action, updateShipmentBtnRef }: AddressProps) => {
  const { t } = useTranslation(['orders-sales', 'default-layout']);

  const { sendAnalyticEvent } = useAnalyticEvents('orders_sales_timeline');

  const { updatePopin, selectedProduct: product } = use(TimelineContext);

  const { showAddressForm } = use(StepInfoContext);

  if (!address) {
    return null;
  }

  const isGetShippingLabel = !!action && action.type === 'getShippingLabel';

  const name = address ? `${address.firstName} ${address.lastName}` : t(`${LANG_PREFIX}YOUR_ADDRESS.ADD_NEW`);

  const fullAddress = address
    ? `${address.line1}, ${address.line2 && `${address.line2},`} ${address.zipCode} ${address.city}, ${address.country
        ?.name}`
    : '';

  const editAddress = () => {
    sendAnalyticEvent('get_shipping_label_modified_address');
    showAddressForm(true);
  };

  const selectShippingDetails = () => {
    sendAnalyticEvent('timeline_select_carrier_and_options');
    updatePopin({ kind: 'GetShippingLabel', product, address });
  };

  return (
    <div className={clsx(styles[CLASSNAME], { [styles[`${CLASSNAME}_hideTopBorderRadius`]]: !isGetShippingLabel })}>
      {!isGetShippingLabel && <div className={styles[`${CLASSNAME}_borderTop`]} />}

      <div className={styles[`${CLASSNAME}_header`]}>
        <p>{t(`${LANG_PREFIX}YOUR_ADDRESS`)}</p>

        <button
          className={styles[`${CLASSNAME}_actionIcon`]}
          onClick={editAddress}
        >
          <Icon
            name="edit-16"
            className={styles[`${CLASSNAME}_icon`]}
            ariaHidden
          />
          <p>{t(`default-layout:HEADER.SEARCH.MODAL.EDIT_BUTTON`)}</p>
        </button>
      </div>

      <div className={styles[`${CLASSNAME}_content`]}>
        <p>{name}</p>
        <p>{fullAddress}</p>
      </div>

      {isGetShippingLabel && (
        <PopupActionView
          ref={updateShipmentBtnRef}
          className={clsx('vc-btn--primary', styles[`${CLASSNAME}_actionButton`])}
          onClick={selectShippingDetails}
        >
          {action.title}
        </PopupActionView>
      )}
    </div>
  );
};

export default AddressCard;
